import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Download, ScanLine, Search } from 'lucide-react';
import {
  AdminMenuImportFilters,
  AdminMenuImportRow,
  AdminMenuImportSummary,
  MenuImportResultFilter,
} from '../types';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import Pagination from '../components/Pagination';
import MenuImportDetailSlideOver from '../components/MenuImportDetailSlideOver';
import MenuImportAiSettingsCard from '../components/MenuImportAiSettingsCard';
import {
  formatCompact,
  formatDateTimeWib,
  formatDuration,
  formatNumber,
  formatRupiah,
  formatUsd,
  menuImportResultBadge,
  thisMonthWib,
} from '../utils/menuImport';

const RESULT_OPTIONS: Array<{ value: MenuImportResultFilter; label: string }> = [
  { value: 'all', label: 'Semua hasil' },
  { value: 'success', label: 'Berhasil' },
  { value: 'failed', label: 'Gagal' },
  { value: 'pending', label: 'Pending' },
];

const inputClass =
  'border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

const StatCard: React.FC<{ label: string; value: React.ReactNode; hint?: React.ReactNode }> = ({ label, value, hint }) => (
  <div className="bg-white shadow rounded-lg p-4">
    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</div>
    <div className="mt-1 text-xl font-semibold text-gray-900">{value}</div>
    {hint ? <div className="mt-1 text-xs text-gray-500">{hint}</div> : null}
  </div>
);

const SmallTable: React.FC<{ title: string; headers: string[]; empty: string; children: React.ReactNode; hasRows: boolean }> = ({
  title,
  headers,
  empty,
  children,
  hasRows,
}) => (
  <div className="bg-white shadow rounded-lg overflow-hidden">
    <div className="px-4 py-3 border-b border-gray-200 text-sm font-semibold text-gray-900">{title}</div>
    {hasRows ? (
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              {headers.map((header, i) => (
                <th
                  key={header}
                  className={`px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider ${i === 0 ? 'text-left' : 'text-right'}`}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">{children}</tbody>
        </table>
      </div>
    ) : (
      <div className="px-4 py-6 text-sm text-gray-500 text-center">{empty}</div>
    )}
  </div>
);

const MenuImports: React.FC = () => {
  const month = useMemo(() => thisMonthWib(), []);
  const [dateFrom, setDateFrom] = useState(month.from);
  const [dateTo, setDateTo] = useState(month.to);
  const [result, setResult] = useState<MenuImportResultFilter>('all');
  const [model, setModel] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage] = useState(15);

  const [rows, setRows] = useState<AdminMenuImportRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<AdminMenuImportSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [knownModels, setKnownModels] = useState<string[]>([]);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<AdminMenuImportRow | null>(null);

  const filters: AdminMenuImportFilters = useMemo(
    () => ({
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      result,
      model: model || undefined,
      search: search || undefined,
    }),
    [dateFrom, dateTo, result, model, search]
  );

  useEffect(() => {
    const timer = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getAdminMenuImports(page, perPage, filters);
      setRows(Array.isArray(response?.data) ? response.data : []);
      setTotal(response?.meta?.total ?? 0);
    } catch (err: any) {
      console.error('Error loading menu imports:', err);
      setRows([]);
      setTotal(0);
      setError(err?.response?.data?.message ?? 'Gagal memuat riwayat import.');
    } finally {
      setLoading(false);
    }
  }, [page, perPage, filters]);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const data = await api.getAdminMenuImportSummary(filters);
      setSummary(data ?? null);
      const models = (data?.byModel ?? []).map((row) => row.model).filter((m): m is string => !!m);
      setKnownModels((prev) => Array.from(new Set([...prev, ...models])).sort());
    } catch (err) {
      console.error('Error loading menu import summary:', err);
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadRows(); }, [loadRows]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await api.exportAdminMenuImports(filters);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `riwayat-import-foto-menu_${dateFrom || 'awal'}_${dateTo || 'akhir'}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting menu imports:', err);
      alert('Gagal export CSV. Coba lagi.');
    } finally {
      setExporting(false);
    }
  };

  const resetFilters = () => {
    setDateFrom(month.from);
    setDateTo(month.to);
    setResult('all');
    setModel('');
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const totals = summary?.totals;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Import Foto Menu</h1>
          <p className="mt-1 text-sm text-gray-500">
            Riwayat baca foto/PDF menu oleh AI: hasil, pemakaian token, dan perkiraan biaya. Waktu dalam WIB.
          </p>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          <Download className="h-4 w-4" />
          {exporting ? 'Mengekspor...' : 'Export CSV'}
        </button>
      </div>

      <MenuImportAiSettingsCard />

      {/* Filter */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex flex-wrap items-end gap-4">
          <label className="text-xs font-medium text-gray-500">
            Dari
            <input
              type="date"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              className={`mt-1 block ${inputClass}`}
            />
          </label>
          <label className="text-xs font-medium text-gray-500">
            Sampai
            <input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              className={`mt-1 block ${inputClass}`}
            />
          </label>
          <label className="text-xs font-medium text-gray-500">
            Hasil
            <select
              value={result}
              onChange={(e) => { setResult(e.target.value as MenuImportResultFilter); setPage(1); }}
              className={`mt-1 block ${inputClass}`}
            >
              {RESULT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label className="text-xs font-medium text-gray-500">
            Model
            <select
              value={model}
              onChange={(e) => { setModel(e.target.value); setPage(1); }}
              className={`mt-1 block ${inputClass}`}
            >
              <option value="">Semua model</option>
              {Array.from(new Set([...knownModels, ...(model ? [model] : [])])).map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </label>
          <div className="flex-1 min-w-[200px]">
            <div className="text-xs font-medium text-gray-500">Cari brand</div>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Nama brand / perusahaan..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className={`pl-9 block w-full ${inputClass}`}
              />
            </div>
          </div>
          <button onClick={resetFilters} className="text-sm text-gray-600 hover:text-gray-900 py-2">
            Reset
          </button>
        </div>
      </div>

      {/* Ringkasan */}
      {summaryLoading && !summary ? (
        <div className="bg-white shadow rounded-lg p-6">
          <LoadingSpinner size="sm" text="Memuat ringkasan..." />
        </div>
      ) : totals ? (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              label="Total Import"
              value={formatNumber(totals.imports)}
              hint={`${formatNumber(totals.units)} foto/halaman · ${formatNumber(totals.itemsRead)} item terbaca`}
            />
            <StatCard
              label="Berhasil / Gagal / Pending"
              value={
                <span>
                  <span className="text-green-700">{formatNumber(totals.success)}</span>
                  {' / '}
                  <span className="text-red-700">{formatNumber(totals.failed)}</span>
                  {' / '}
                  <span className="text-yellow-700">{formatNumber(totals.pending)}</span>
                </span>
              }
              hint={
                totals.successRate === null || totals.successRate === undefined
                  ? 'Tingkat berhasil: —'
                  : `Tingkat berhasil ${totals.successRate.toLocaleString('id-ID')}% (dari yang selesai)`
              }
            />
            <StatCard
              label="Total Token"
              value={formatCompact(totals.tokens?.total)}
              hint={`In ${formatCompact(totals.tokens?.input)} · Out ${formatCompact(totals.tokens?.output)} · Cache R ${formatCompact(
                totals.tokens?.cacheRead
              )} / W ${formatCompact(totals.tokens?.cacheWrite)}`}
            />
            <StatCard
              label="Total Biaya"
              value={formatRupiah(totals.cost?.idr)}
              hint={
                <>
                  {formatUsd(totals.cost?.usd)} · kurs {formatNumber(totals.usdToIdr)}
                  {totals.unpricedImports > 0 ? (
                    <span className="block text-yellow-700">{totals.unpricedImports} import modelnya belum ada harga (tidak dihitung)</span>
                  ) : null}
                </>
              }
            />
            <StatCard
              label="Rata-rata Biaya / Import"
              value={formatRupiah(totals.avgCostPerImport?.idr)}
              hint={formatUsd(totals.avgCostPerImport?.usd)}
            />
            <StatCard
              label="Biaya / Import Berhasil"
              value={formatRupiah(totals.avgCostPerSuccess?.idr)}
              hint="Termasuk biaya import yang gagal"
            />
            <StatCard
              label="Biaya / Item Terbaca"
              value={formatRupiah(totals.avgCostPerItem?.idr)}
              hint={formatUsd(totals.avgCostPerItem?.usd)}
            />
            <StatCard label="Rata-rata Durasi" value={formatDuration(totals.avgDurationSeconds)} hint="Dari antre sampai hasil siap/gagal" />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <SmallTable title="Per Model" headers={['Model', 'Import', 'Token', 'Biaya']} empty="Belum ada data." hasRows={(summary?.byModel ?? []).length > 0}>
              {(summary?.byModel ?? []).map((row) => (
                <tr key={row.model ?? '-'}>
                  <td className="px-4 py-2 text-gray-900">{row.model ?? '(tanpa model)'}</td>
                  <td className="px-4 py-2 text-right">{formatNumber(row.imports)}</td>
                  <td className="px-4 py-2 text-right">{formatCompact(row.tokens?.total)}</td>
                  <td className="px-4 py-2 text-right">
                    {formatRupiah(row.cost?.idr)}
                    <div className="text-xs text-gray-500">{formatUsd(row.cost?.usd)}</div>
                  </td>
                </tr>
              ))}
            </SmallTable>
            <SmallTable title="Top 10 Brand (biaya)" headers={['Brand', 'Import', 'Biaya']} empty="Belum ada data." hasRows={(summary?.topCompanies ?? []).length > 0}>
              {(summary?.topCompanies ?? []).map((row, index) => (
                <tr key={row.company?.id ?? index}>
                  <td className="px-4 py-2 text-gray-900">{row.company?.name ?? '—'}</td>
                  <td className="px-4 py-2 text-right">
                    {formatNumber(row.imports)}
                    <div className="text-xs text-gray-500">{formatNumber(row.success)} berhasil</div>
                  </td>
                  <td className="px-4 py-2 text-right">
                    {formatRupiah(row.cost?.idr)}
                    <div className="text-xs text-gray-500">{formatUsd(row.cost?.usd)}</div>
                  </td>
                </tr>
              ))}
            </SmallTable>
            <SmallTable title="Kode Error" headers={['Kode', 'Jumlah']} empty="Tidak ada import gagal." hasRows={(summary?.errorCodes ?? []).length > 0}>
              {(summary?.errorCodes ?? []).map((row) => (
                <tr key={row.code}>
                  <td className="px-4 py-2 font-mono text-xs text-gray-900">{row.code}</td>
                  <td className="px-4 py-2 text-right">{formatNumber(row.count)}</td>
                </tr>
              ))}
            </SmallTable>
          </div>
        </>
      ) : null}

      {/* Tabel */}
      {loading ? (
        <div className="bg-white shadow rounded-lg p-8">
          <LoadingSpinner size="md" text="Memuat riwayat import..." />
        </div>
      ) : error ? (
        <div className="bg-white shadow rounded-lg p-6 text-sm text-red-700">{error}</div>
      ) : rows.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <ScanLine className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Belum ada import</h3>
          <p className="mt-1 text-sm text-gray-500">Coba ubah rentang tanggal atau filter.</p>
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['Waktu', 'Brand', 'User', 'File', 'Unit', 'Hasil', 'Model', 'Token', 'Biaya', 'Durasi'].map((header) => (
                    <th key={header} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rows.map((row) => {
                  const files = row.files ?? [];
                  return (
                    <tr key={row.id} onClick={() => setSelected(row)} className="hover:bg-gray-50 cursor-pointer">
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDateTimeWib(row.createdAt)}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{row.company?.name ?? '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{row.user?.name ?? '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <div className="max-w-[160px] truncate" title={files.map((f) => f.name).join(', ')}>
                          {files.length > 0 ? files.map((f) => f.name).join(', ') : '—'}
                        </div>
                        <div className="text-xs text-gray-400">{files.length} file</div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">{formatNumber(row.units)}</td>
                      <td className="px-4 py-3 text-sm">
                        {menuImportResultBadge(row.result, row.resultLabel)}
                        {row.errorCode ? <div className="mt-1 text-xs font-mono text-red-600">{row.errorCode}</div> : null}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-700 font-mono whitespace-nowrap">{row.model ?? '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap" title={`In ${formatNumber(row.tokens?.input)} · Out ${formatNumber(row.tokens?.output)} · Cache R ${formatNumber(row.tokens?.cacheRead)} / W ${formatNumber(row.tokens?.cacheWrite)}`}>
                        {formatCompact(row.tokens?.total)}
                      </td>
                      <td className="px-4 py-3 text-sm whitespace-nowrap">
                        {row.cost?.priced === false ? (
                          <span className="text-xs text-yellow-700">Belum ada harga</span>
                        ) : (
                          <>
                            <div className="text-gray-900">{formatRupiah(row.cost?.idr)}</div>
                            <div className="text-xs text-gray-500">{formatUsd(row.cost?.usd)}</div>
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{formatDuration(row.durationSeconds)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 border-t border-gray-200">
            <Pagination currentPage={page} total={total} perPage={perPage} onPageChange={setPage} />
          </div>
        </div>
      )}

      <MenuImportDetailSlideOver row={selected} isOpen={selected !== null} onClose={() => setSelected(null)} />
    </div>
  );
};

export default MenuImports;
